import React, { useCallback, useEffect, useState } from 'react';
import { useGetBillingInvoicesQuery } from '@features/billing/store';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { OrgInvoiceResponse } from '@store/generated/billing';
import { format } from 'date-fns/format';

import { useLazyGetInvoiceUrlsQuery } from '../store/api';
import { useStripeConnect } from '../store/hooks';

const ROWS_PER_PAGE = 10;

interface InvoiceStatusChipProps {
  status: string;
}

const InvoiceStatusChip: React.FC<InvoiceStatusChipProps> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status.toLowerCase()) {
      case 'paid':
        return { label: 'Paid', color: 'success' as const };
      case 'open':
        return { label: 'Open', color: 'warning' as const };
      case 'void':
        return { label: 'Void', color: 'default' as const };
      case 'uncollectible':
        return { label: 'Uncollectible', color: 'error' as const };
      case 'draft':
        return { label: 'Draft', color: 'info' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const { label, color } = getStatusConfig();
  return <Chip label={label} color={color} size="small" />;
};

export const StripeInvoicesTable: React.FC = () => {
  const { hasStripeAccount } = useStripeConnect();
  const { payment } = useEventTracking();
  const [currentPage, setCurrentPage] = useState(0);
  const [fetchInvoiceUrls] = useLazyGetInvoiceUrlsQuery();
  const [loadingUrlFor, setLoadingUrlFor] = useState<{
    id: string;
    action: 'view' | 'download';
  } | null>(null);

  const offset = currentPage * ROWS_PER_PAGE;

  const { data, isLoading, isFetching, error } = useGetBillingInvoicesQuery(
    { limit: ROWS_PER_PAGE, offset },
    { skip: !hasStripeAccount },
  );

  // Track invoice table view when data loads
  useEffect(() => {
    if (data?.invoices && data.invoices.length > 0) {
      payment.invoicesViewed({});
    }
  }, [data?.invoices, payment]);

  // Fetch fresh URL on demand then open it
  const handleUrlAction = useCallback(
    async (invoice: OrgInvoiceResponse, urlKey: 'payment_url' | 'pdf_url') => {
      const action = urlKey === 'payment_url' ? 'view' : 'download';
      setLoadingUrlFor({ id: invoice.id, action });
      try {
        const result = await fetchInvoiceUrls({
          patientId: invoice.patient_id,
          invoiceId: invoice.id,
        }).unwrap();
        const url = result[urlKey];
        if (url) window.open(url, '_blank');
      } catch {
        // URL fetch failed
      } finally {
        setLoadingUrlFor(null);
      }
    },
    [fetchInvoiceUrls],
  );

  // Don't render if Stripe is not connected
  if (!hasStripeAccount) {
    return null;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load invoices. Please try again later.</Typography>
      </Box>
    );
  }

  const invoices = data?.invoices || [];
  const hasMore = invoices.length >= ROWS_PER_PAGE;
  const isFirstPage = currentPage === 0;

  const handleNextPage = () => {
    if (hasMore) setCurrentPage(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    if (!isFirstPage) setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const formatCurrency = (amountInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amountInCents / 100);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (isLoading && !isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {invoices.length === 0 && currentPage === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No invoices yet. Invoices will appear here once you start processing payments.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice: OrgInvoiceResponse) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {invoice.stripe_invoice_id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(invoice.created_at)}</TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(invoice.amount_cents)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleUrlAction(invoice, 'payment_url')}
                            disabled={loadingUrlFor?.id === invoice.id && loadingUrlFor.action === 'view'}
                          >
                            {loadingUrlFor?.id === invoice.id && loadingUrlFor.action === 'view' ? (
                              <CircularProgress size={18} />
                            ) : (
                              <OpenInNewIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleUrlAction(invoice, 'pdf_url')}
                            disabled={loadingUrlFor?.id === invoice.id && loadingUrlFor.action === 'download'}
                          >
                            {loadingUrlFor?.id === invoice.id && loadingUrlFor.action === 'download' ? (
                              <CircularProgress size={18} />
                            ) : (
                              <PdfIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Offset-based pagination controls */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={2}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage + 1}
              {isFetching && ' (Loading...)'}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePreviousPage}
                disabled={isFirstPage || isFetching}
              >
                Previous
              </Button>
              <Button
                size="small"
                endIcon={<NavigateNextIcon />}
                onClick={handleNextPage}
                disabled={!hasMore || isFetching}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};
